import { getCache, type ServiceType } from './indexeddb-cache';
import { dedup } from './request-dedup';

export const LIVE_RESOURCE_TTL_MS = 15 * 60 * 1000;
export const HISTORICAL_RESOURCE_TTL_MS = 6 * 60 * 60 * 1000;
export const METADATA_RESOURCE_TTL_MS = 60 * 60 * 1000;

export interface ResourceSnapshot<T> {
  value: T;
  fetchedAt: number;
  freshUntil: number;
}

const lockTails = new Map<string, Promise<void>>();

/** Same-tab single flight, plus cross-tab serialization followed by a cache read.
 * Locks are released on success AND failure. No credential material enters keys.
 */
export const withProviderResourceLock = <T>(
  key: string,
  work: () => Promise<T>,
  forceRefresh = false,
  requestKey = ''
): Promise<T> => dedup(`resource:${key}:${requestKey}:${forceRefresh ? 'refresh' : 'read'}`, async () => {
  const previous = lockTails.get(key) ?? Promise.resolve();
  const result = previous.then(async () => {
    if (typeof navigator !== 'undefined' && navigator.locks?.request) {
      return navigator.locks.request(`usage-dashboard:${key}`, work);
    }
    return work();
  });
  const tail = result.then(() => {}, () => {});
  lockTails.set(key, tail);
  try {
    return await result;
  } finally {
    if (lockTails.get(key) === tail) lockTails.delete(key);
  }
});

/** Persistence, freshness and concurrency belong to the resource, not a view.
 * Failed responses are returned to the caller but never become fresh cache hits.
 */
export const readProviderResource = <T>(options: {
  service: ServiceType;
  endpoint: string;
  params: Record<string, string | number>;
  credentialRevision: string;
  ttl: number;
  forceRefresh?: boolean;
  load: () => Promise<T>;
  isComplete: (value: T) => boolean;
}): Promise<ResourceSnapshot<T>> => {
  const { service, endpoint, credentialRevision, ttl, forceRefresh = false, load, isComplete } = options;
  const params = { ...options.params, credentialRevision };
  const key = JSON.stringify([service, endpoint, params]);
  return withProviderResourceLock(key, async () => {
    const cache = getCache();
    if (!forceRefresh) {
      const cached = await cache.get<ResourceSnapshot<T>>(service, endpoint, params);
      if (cached && cached.freshUntil > Date.now()) return cached;
    } else {
      // A failed forced refresh must not leave a still-fresh success behind
      // that makes the next ordinary Retry appear to have recovered.
      await cache.delete(service, endpoint, params);
    }
    const fetchedAt = Date.now();
    const value = await load();
    const snapshot = { value, fetchedAt, freshUntil: fetchedAt + ttl };
    if (isComplete(value) && snapshot.freshUntil > Date.now()) {
      await cache.set(service, endpoint, snapshot, params, snapshot.freshUntil - Date.now());
    }
    return snapshot;
  }, forceRefresh);
};
